import type { StorageReference, StorageService, UploadResult } from "@infrastructure/interfaces/database.interface";
import logger from "@logger";
import { deleteObject, getDownloadURL, listAll, ref, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { storage } from "../config/firebase";

export class FirebaseStorageService implements StorageService {
  // Upload file to storage
  async uploadFile(path: string, file: File, metadata?: Record<string, unknown>): Promise<UploadResult> {
    try {
      const storageRef = ref(storage, path);
      return await uploadBytes(storageRef, file, metadata);
    } catch (error) {
      logger.error("Error uploading file:", error);
      throw error;
    }
  }

  // Get download URL for file
  async getDownloadURL(path: string): Promise<string> {
    try {
      const storageRef = ref(storage, path);
      return await getDownloadURL(storageRef);
    } catch (error) {
      logger.error("Error getting download URL:", error);
      throw error;
    }
  }

  // Delete file from storage
  async deleteFile(path: string): Promise<void> {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (error) {
      logger.error("Error deleting file:", error);
      throw error;
    }
  }

  // List all files in a directory
  async listFiles(path: string): Promise<string[]> {
    try {
      const storageRef = ref(storage, path);
      const result = await listAll(storageRef);
      return result.items.map((item) => item.fullPath);
    } catch (error) {
      logger.error("Error listing files:", error);
      throw error;
    }
  }

  // Upload profile photo with optimisation
  async uploadProfilePhoto(userId: string, file: File, photoId: string): Promise<string> {
    try {
      const path = `users/${userId}/photos/${photoId}`;
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          userId,
          photoId,
          uploadedAt: new Date().toISOString(),
        },
      });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          null,
          (error) => reject(error),
          () => resolve()
        );
      });

      return await this.getDownloadURL(path);
    } catch (error) {
      logger.error("Error uploading profile photo:", error);
      throw error;
    }
  }

  // Upload chat media
  async uploadChatMedia(chatId: string, file: File, messageId: string): Promise<string> {
    try {
      const path = `chats/${chatId}/media/${messageId}`;
      await this.uploadFile(path, file, {
        contentType: file.type,
        customMetadata: {
          chatId,
          messageId,
          uploadedAt: new Date().toISOString(),
        },
      });

      return await this.getDownloadURL(path);
    } catch (error) {
      logger.error("Error uploading chat media:", error);
      throw error;
    }
  }

  // Get storage reference
  getStorageReference(path: string): StorageReference {
    return ref(storage, path);
  }

  // Generate unique file path
  generateFilePath(collection: string, documentId: string, fileName: string): string {
    const timestamp = Date.now();
    const extension = fileName.split(".").pop();
    return `${collection}/${documentId}/${timestamp}.${extension}`;
  }
}

// Export singleton instance
export const firebaseStorageService = new FirebaseStorageService();
export default firebaseStorageService;
